/**
 * Canvas Area Component
 * 
 * Center panel showing fields for the selected step/variant.
 * Includes variant tabs for A/B testing and drag-and-drop field reordering.
 */

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical,
  Plus,
  Trash2,
  Settings,
  Shield,
  Type,
  Mail,
  Phone,
  Hash,
  Calendar,
  Clock,
  AlignLeft,
  List,
  CircleDot,
  CheckSquare,
  Upload,
  Beaker,
} from 'lucide-react';
import {
  useFormBuilder,
  useSelectedStep,
  useSelectedVariant,
} from '../../contexts';
import type { FormField, FieldType, StepVariant } from '@formflow/shared';
import './CanvasArea.css';

// =============================================================================
// Field Type Icons
// =============================================================================

const fieldTypeIcons: Record<FieldType, React.ReactNode> = {
  text: <Type size={16} />,
  email: <Mail size={16} />,
  phone: <Phone size={16} />,
  number: <Hash size={16} />,
  date: <Calendar size={16} />,
  time: <Clock size={16} />,
  datetime: <Calendar size={16} />,
  textarea: <AlignLeft size={16} />,
  select: <List size={16} />,
  radio: <CircleDot size={16} />,
  checkbox: <CheckSquare size={16} />,
  file: <Upload size={16} />,
};

// =============================================================================
// Sortable Field Item
// =============================================================================

interface SortableFieldItemProps {
  field: FormField;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}

function SortableFieldItem({
  field,
  isSelected,
  onSelect,
  onDelete,
}: SortableFieldItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(`Delete field "${field.label}"?`)) {
      onDelete();
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`field-item ${isSelected ? 'selected' : ''} ${field.is_phi ? 'phi-field' : ''}`}
      onClick={onSelect}
    >
      <button
        className="drag-handle"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
      >
        <GripVertical size={16} />
      </button>

      <div className="field-icon">
        {fieldTypeIcons[field.field_type]}
      </div>

      <div className="field-content">
        <div className="field-label">
          {field.label}
          {field.required && <span className="required-marker">*</span>}
        </div>
        <div className="field-meta">
          <span className="field-type">{field.field_type}</span>
          {field.is_phi && (
            <span className="phi-badge">
              <Shield size={10} />
              PHI
            </span>
          )}
        </div>
      </div>

      <div className="field-actions">
        <button
          className="btn-icon-sm"
          onClick={handleDelete}
          aria-label="Delete field"
        >
          <Trash2 size={14} />
        </button>
        <button
          className="btn-icon-sm"
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
          aria-label="Field settings"
        >
          <Settings size={14} />
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// Variant Tabs
// =============================================================================

interface VariantTabsProps {
  variants: StepVariant[];
  selectedVariantId: string | null;
  onSelect: (variantId: string) => void;
  onAddVariant: () => void;
}

function VariantTabs({
  variants,
  selectedVariantId,
  onSelect,
  onAddVariant,
}: VariantTabsProps) {
  if (variants.length <= 1) return null;

  return (
    <div className="variant-tabs">
      <div className="tabs-list">
        {variants.map((variant) => (
          <button
            key={variant.id}
            className={`tab ${selectedVariantId === variant.id ? 'active' : ''}`}
            onClick={() => onSelect(variant.id)}
          >
            {variant.name}
            {variant.is_control && <span className="control-badge">Control</span>}
            <span className="weight-badge">{variant.weight}%</span>
          </button>
        ))}
        <button className="tab add-tab" onClick={onAddVariant}>
          <Plus size={14} />
          Add Variant
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// Canvas Area
// =============================================================================

export function CanvasArea() {
  const {
    state,
    selectField,
    selectVariant,
    deleteField,
    reorderFields,
    toggleFieldTypeSelector,
    addVariant,
  } = useFormBuilder();
  const { selectedStepId, selectedVariantId, selectedFieldId } = state;
  const step = useSelectedStep();
  const variant = useSelectedVariant();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    if (!variant) return;

    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = variant.fields.findIndex((f) => f.id === active.id);
      const newIndex = variant.fields.findIndex((f) => f.id === over.id);
      const newOrder = arrayMove(variant.fields, oldIndex, newIndex).map((f) => f.id);
      await reorderFields(variant.id, newOrder);
    }
  };

  const handleAddVariant = async () => {
    if (!step) return;
    try {
      await addVariant(step.id, {
        name: `Variant ${step.variants.length + 1}`,
        weight: Math.floor(100 / (step.variants.length + 1)),
      });
    } catch (error) {
      console.error('Failed to add variant:', error);
    }
  };

  const handleAddField = () => {
    toggleFieldTypeSelector(true);
  };

  // Empty state - no step selected
  if (!selectedStepId || !step) {
    return (
      <main className="canvas-area">
        <div className="canvas-empty">
          <Beaker size={48} />
          <h3>No Step Selected</h3>
          <p>Select a step from the left panel to start adding fields.</p>
        </div>
      </main>
    );
  }

  // Auto-select first variant if none selected
  if (!selectedVariantId && step.variants[0]) {
    selectVariant(step.variants[0].id);
    return null;
  }

  return (
    <main className="canvas-area">
      <div className="canvas-header">
        <div className="step-title">
          <h2>{step.name}</h2>
          {step.description && <p>{step.description}</p>}
        </div>
        
        <div className="ab-testing-toggle">
          {step.variants.length === 1 && (
            <button className="btn btn-sm btn-outline" onClick={handleAddVariant}>
              <Beaker size={14} />
              Enable A/B Testing
            </button>
          )}
        </div>
      </div>

      <VariantTabs
        variants={step.variants}
        selectedVariantId={selectedVariantId}
        onSelect={selectVariant}
        onAddVariant={handleAddVariant}
      />

      <div className="canvas-content">
        {variant && variant.fields.length === 0 ? (
          <div className="fields-empty">
            <div className="empty-illustration">
              <Type size={32} />
            </div>
            <h3>No Fields Yet</h3>
            <p>Start building your form by adding fields.</p>
            <button className="btn btn-primary" onClick={handleAddField}>
              <Plus size={18} />
              Add First Field
            </button>
          </div>
        ) : variant && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={variant.fields.map((f) => f.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="fields-list">
                {variant.fields.map((field) => (
                  <SortableFieldItem
                    key={field.id}
                    field={field}
                    isSelected={selectedFieldId === field.id}
                    onSelect={() => selectField(field.id)}
                    onDelete={() => deleteField(field.id)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        <button
          className="add-field-button"
          onClick={handleAddField}
        >
          <Plus size={18} />
          Add Field
        </button>
      </div>
    </main>
  );
}

export default CanvasArea;
